import { useAppSelector } from '@/hooks'
import { thermostatsCollection } from '@/reducers/thermostats'
import { thermostatDemandResponseEventsCollection } from '@/reducers/thermostatDemandResponseEvents'
import { eventIsCurrentlyActive } from '@/types/deviceDemandResponseEvent'
import selectLatestAdjustmentForThermostat from '../../../../selectors/selectLatestAdjustmentForThermostat'
import {
  ThermostatAdjustment,
  ThermostatAdjustmentSource,
} from '@/types/thermostatAdjustment'
import { match } from 'ts-pattern'
import { CostCategoryMode } from './components/CostCategoryModeCard'
import { toTitleCase } from '@/utils/string'
import { getCostCategoryTypeFromRange } from './utils/costCategoryMappings'

export interface ThermostatStatusDetails {
  mode: CostCategoryMode | null
  badgeText?: string
  temperature?: string
  title?: string
}

export interface ThermostatStatusModeCalculation {
  thermostatStatus: ThermostatStatusDetails
}

interface UseThermostatStatusModesParams {
  thermostatId: number
  isInInactivePeriod: boolean
  allCostCategoriesSetToZero: boolean
  saveMoneyEnabled: boolean
  forcedCostCategoryControlState?: boolean | null
  isDREventActive?: boolean
}

export default function useThermostatStatusModes({
  thermostatId,
  isInInactivePeriod,
  allCostCategoriesSetToZero,
  saveMoneyEnabled,
  forcedCostCategoryControlState,
  isDREventActive,
}: UseThermostatStatusModesParams): ThermostatStatusModeCalculation {
  // Fetch thermostat with adjustments
  thermostatsCollection.useFetchAnnotation(thermostatId, 'adjustments')

  // Fetch DR events for this thermostat
  const { data: drEvents } = thermostatDemandResponseEventsCollection.useFetch()

  // Find active DR event for this thermostat (enrolled and currently within time window)
  const activeDrEvent = drEvents?.find(
    (event) =>
      event.thermostat_id === thermostatId &&
      event.enrolled &&
      eventIsCurrentlyActive(event),
  )

  // Use passed isDREventActive or calculate it from the DR event
  const isActiveDREvent = Boolean(activeDrEvent)

  // Get latest adjustment using existing selector
  const latestAdjustment = useAppSelector((state) =>
    selectLatestAdjustmentForThermostat(state, thermostatId),
  )

  const getBadgeTextFromAdjustment = (
    adjustment: ThermostatAdjustment | null | undefined,
  ): string | undefined => {
    if (!adjustment?.source) return undefined

    return match(adjustment.source as ThermostatAdjustmentSource)
      .with('COST_CATEGORY', () => {
        const heatAdjustment =
          adjustment.heat_setpoint && adjustment.previous_heat_setpoint
            ? adjustment.heat_setpoint - adjustment.previous_heat_setpoint
            : null
        const coolAdjustment =
          adjustment.cool_setpoint && adjustment.previous_cool_setpoint
            ? adjustment.cool_setpoint - adjustment.previous_cool_setpoint
            : null

        if (coolAdjustment !== null) {
          if (coolAdjustment === 0) {
            return 'No adjustment made'
          }
          return `Adjusted ${coolAdjustment > 0 ? '+' : ''}${coolAdjustment}°`
        }
        if (heatAdjustment !== null) {
          if (heatAdjustment === 0) {
            return 'No adjustment made'
          }
          return `Adjusted ${heatAdjustment > 0 ? '+' : ''}${heatAdjustment}°`
        }
        return 'Cost category adjustment'
      })
      .otherwise(() => undefined)
  }

  const getTemperatureFromAdjustment = (
    adjustment: ThermostatAdjustment | null | undefined,
  ): string | undefined => {
    if (!adjustment) return undefined

    // Prefer cool setpoint, fallback to heat setpoint
    if (adjustment.cool_setpoint) {
      return adjustment.cool_setpoint.toString()
    }
    if (adjustment.heat_setpoint) {
      return adjustment.heat_setpoint.toString()
    }
    return undefined
  }

  const getCostCategoryModeFromAdjustment = (
    adjustment: ThermostatAdjustment | null | undefined,
  ): CostCategoryMode | null => {
    if (!adjustment?.cost_category_control) return null

    const categoryType = getCostCategoryTypeFromRange({
      min_cost_cents: adjustment.cost_category_control.min_cost_cents,
      max_cost_cents: adjustment.cost_category_control.max_cost_cents,
    })

    return categoryType as CostCategoryMode
  }

  const getTitleFromAdjustment = (
    adjustment: ThermostatAdjustment | null | undefined,
    mode: CostCategoryMode | null,
  ): string | undefined => {
    if (mode === 'adjustment') {
      return 'Thermostat adjustment made'
    }

    if (mode === 'off') {
      return 'Inactive'
    }

    if (
      !adjustment?.cost_category_control ||
      adjustment.source !== 'COST_CATEGORY'
    ) {
      return undefined
    }

    // Use the same logic as getCostCategoryModeFromAdjustment
    const categoryType = getCostCategoryModeFromAdjustment(adjustment)

    return categoryType ? `${toTitleCase(categoryType)} Cost Hour` : undefined
  }

  const hasAdjustment = Boolean(latestAdjustment)

  const mode = match({
    isInInactivePeriod,
    forcedCostCategoryControlState,
    allCostCategoriesSetToZero,
    saveMoneyEnabled,
    hasAdjustment,
    isActiveDREvent,
  })
    .with({ forcedCostCategoryControlState: false }, () => 'off' as const)
    .with({ isInInactivePeriod: true }, () => 'off' as const)
    .with({ allCostCategoriesSetToZero: true }, () => 'off' as const)
    .with({ isActiveDREvent: true }, () => 'off' as const)
    .with({ saveMoneyEnabled: false }, () => null)
    .with({ hasAdjustment: true }, () => {
      // If there's an adjustment, check if it's a cost category adjustment
      if (latestAdjustment?.source === 'COST_CATEGORY') {
        const costCategoryMode =
          getCostCategoryModeFromAdjustment(latestAdjustment)
        // If we have a cost category control, use its mode, otherwise treat as adjustment
        return costCategoryMode || ('adjustment' as const)
      }
      // For other adjustment types (manual, remote control, etc.)
      return 'adjustment' as const
    })
    .otherwise(() => null)

  const badgeText = match(mode)
    .with('off', () => {
      // Check for forced state first
      if (forcedCostCategoryControlState !== null) {
        return 'Due to program'
      }
      // Check for DR event
      if (isActiveDREvent) {
        return 'Due to DR event'
      }
      if (isInInactivePeriod && allCostCategoriesSetToZero) {
        return 'Due to shifts & inactive times'
      }
      if (isInInactivePeriod) {
        return 'Due to your inactive times'
      }
      if (allCostCategoriesSetToZero) {
        return 'Due to all shifts set to 0°'
      }
      return undefined
    })
    .with('adjustment', () => {
      return getBadgeTextFromAdjustment(latestAdjustment)
    })
    .otherwise(() => {
      // Check for forced state for active modes too
      if (forcedCostCategoryControlState !== null) {
        return 'Due to program'
      }
      return getBadgeTextFromAdjustment(latestAdjustment)
    })

  const temperature = getTemperatureFromAdjustment(latestAdjustment)
  const title = getTitleFromAdjustment(latestAdjustment, mode)

  return {
    thermostatStatus: {
      mode,
      badgeText,
      temperature,
      title,
    },
  }
}
